import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Alert, ProgressBar, Badge, Form, Tabs, Tab } from 'react-bootstrap';
import { getAvisos, getMedios } from '../services/apiService';
import { format, startOfMonth, endOfMonth, subMonths, getMonth, getYear, addDays, parse, isSunday, differenceInDays, startOfWeek, getISOWeek, getISOWeekYear, endOfWeek, startOfDay, isSameDay, isFirstDayOfMonth, subDays, subWeeks, startOfYear, endOfYear, endOfDay, subYears } from 'date-fns';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  ArcElement,
  Title, 
  Tooltip, 
  Legend
);

const Dashboard = () => {
  // Estados para datos generales
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para datos de avisos
  const [datosAvisos, setDatosAvisos] = useState(null);
  const [topEjecutivos, setTopEjecutivos] = useState([]);
  const [topClientes, setTopClientes] = useState([]);
  const [distribucionEstados, setDistribucionEstados] = useState([]);
  const [tendenciaVentas, setTendenciaVentas] = useState([]);
  const [rendimientoMedio, setRendimientoMedio] = useState([]);
  
  // Estados para comparación
  const [modoComparacion, setModoComparacion] = useState(false);
  const [comparacionSeleccionada, setComparacionSeleccionada] = useState('');
  const [datosComparacion, setDatosComparacion] = useState(null);
  const [loadingComparacion, setLoadingComparacion] = useState(false);
  
  // Estados para filtros
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState('mes_actual'); // Mes actual por defecto
  const [periodoTemp, setPeriodoTemp] = useState('mes_actual'); // Estado temporal para el selector
  const [medioSeleccionado, setMedioSeleccionado] = useState('');
  const [mediosDisponibles, setMediosDisponibles] = useState([]);
  const [periodoMostrado, setPeriodoMostrado] = useState('');
  
  // Estados para periodo personalizado
  const [mostrarPeriodoPersonalizado, setMostrarPeriodoPersonalizado] = useState(false);
  const [fechaInicioPersonalizada, setFechaInicioPersonalizada] = useState('');
  const [fechaFinPersonalizada, setFechaFinPersonalizada] = useState('');
  const [errorPeriodoPersonalizado, setErrorPeriodoPersonalizado] = useState('');
  
  // Colores para gráficos
  const colores = [
    'rgba(54, 162, 235, 0.8)',
    'rgba(255, 99, 132, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)',
    'rgba(199, 199, 199, 0.8)',
    'rgba(83, 102, 255, 0.8)',
    'rgba(40, 159, 64, 0.8)',
    'rgba(210, 105, 30, 0.8)'
  ];
  
  // Nombres de meses
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  
  // Efecto para cargar datos iniciales
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const mediosResponse = await getMedios();
        if (mediosResponse.success) {
          setMediosDisponibles(mediosResponse.data);
        }
        
        // Cargar datos del mes actual por defecto
        const fechaActual = new Date();
        const primerDiaMes = startOfMonth(fechaActual);
        const ultimoDiaMes = endOfMonth(fechaActual);
        
        // Establecer el título del periodo para mostrarlo en la UI
        const tituloPeriodo = `Mes actual (${format(primerDiaMes, 'dd/MM/yyyy')} - ${format(ultimoDiaMes, 'dd/MM/yyyy')})`;
        setPeriodoMostrado(tituloPeriodo);
        
        const filtros = {
          fechaInicio: format(primerDiaMes, 'yyyy-MM-dd'),
          fechaFin: format(ultimoDiaMes, 'yyyy-MM-dd'),
          limit: 9999 // Solicitar un número muy alto de registros
        };
        
        console.log('Cargando datos iniciales del mes actual:', filtros);
        
        const response = await getAvisos(filtros);
        if (response.success) {
          console.log(`Recibidos ${response.data.length} avisos del servidor (datos iniciales)`);
          procesarDatosAvisos(response.data, primerDiaMes, ultimoDiaMes);
        } else {
          setError('No se pudieron cargar los datos de avisos.');
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error);
        setError('No se pudieron cargar los datos iniciales. Por favor, intente de nuevo.');
        setIsLoading(false);
      }
    };
    
    cargarDatos();
  }, []); // Eliminar comparacionSeleccionada de las dependencias
  
  // Ya no recargamos automáticamente cuando cambian los filtros
  // Esto se hará sólo cuando el usuario haga clic en el botón "Actualizar datos"
  
  // Función para cargar datos del periodo seleccionado
  const cargarDatosPeriodo = async (periodo, medio = '') => {
    setIsLoading(true);
    setError(null);
    setErrorPeriodoPersonalizado('');
    
    // Limpiar datos de comparación al inicio
    setDatosComparacion(null);
    setModoComparacion(false);
    
    try {
      let fechaFin, fechaInicio;
      let tituloPeriodo = '';
      
      // Si se seleccionó un period personalizado
      if (periodo === 'personalizado') {
        if (!fechaInicioPersonalizada || !fechaFinPersonalizada) {
          setErrorPeriodoPersonalizado('Debe seleccionar ambas fechas para el periodo personalizado');
          setIsLoading(false);
          return;
        }
        
        try {
          const [anioInicio, mesInicio, diaInicio] = fechaInicioPersonalizada.split('-').map(Number);
          const [anioFin, mesFin, diaFin] = fechaFinPersonalizada.split('-').map(Number);
          
          fechaInicio = new Date(anioInicio, mesInicio - 1, diaInicio);
          fechaFin = new Date(anioFin, mesFin - 1, diaFin, 23, 59, 59);
          
          if (isNaN(fechaInicio.getTime()) || isNaN(fechaFin.getTime())) {
            setErrorPeriodoPersonalizado('Las fechas seleccionadas no son válidas');
            setIsLoading(false);
            return;
          }
          
          if (fechaInicio > fechaFin) {
            setErrorPeriodoPersonalizado('La fecha de inicio debe ser anterior a la fecha de fin');
            setIsLoading(false);
            return;
          }
          
          tituloPeriodo = `Periodo personalizado (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(fechaFin, 'dd/MM/yyyy')})`;
        } catch (error) {
          console.error('Error al parsear fechas:', error);
          setErrorPeriodoPersonalizado('Error al procesar las fechas seleccionadas');
          setIsLoading(false);
          return;
        }
      } else {
        const hoy = new Date();
        const ayer = subDays(hoy, 1);
        const inicioSemanaActual = startOfWeek(hoy, { weekStartsOn: 1 }); // Lunes
        const inicioSemanaPasada = subWeeks(inicioSemanaActual, 1);
        const finSemanaPasada = subDays(inicioSemanaActual, 1); // Domingo anterior
        
        switch(periodo) {
          case 'mes_actual':
            fechaInicio = startOfMonth(hoy);
            fechaFin = endOfMonth(hoy);
            tituloPeriodo = `Mes actual (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(fechaFin, 'dd/MM/yyyy')})`;
            break;
            
          case 'hoy':
            fechaInicio = startOfDay(hoy);
            fechaFin = endOfDay(hoy);
            tituloPeriodo = `Hoy (${format(hoy, 'dd/MM/yyyy')})`;
            break;
            
          case 'ayer':
            fechaInicio = startOfDay(ayer);
            fechaFin = endOfDay(ayer);
            tituloPeriodo = `Ayer (${format(ayer, 'dd/MM/yyyy')})`;
            break;
            
          case 'esta_semana':
            fechaInicio = inicioSemanaActual;
            fechaFin = endOfDay(hoy);
            tituloPeriodo = `Esta semana (${format(inicioSemanaActual, 'dd/MM/yyyy')} - ${format(hoy, 'dd/MM/yyyy')})`;
            break;
            
          case 'semana_pasada':
            fechaInicio = inicioSemanaPasada;
            fechaFin = finSemanaPasada;
            tituloPeriodo = `Semana pasada (${format(inicioSemanaPasada, 'dd/MM/yyyy')} - ${format(finSemanaPasada, 'dd/MM/yyyy')})`;
            break;
            
          case 'ultimos_7_dias':
            fechaInicio = subDays(hoy, 6);
            fechaFin = endOfDay(hoy);
            tituloPeriodo = `Últimos 7 días (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(hoy, 'dd/MM/yyyy')})`;
            break;
            
          case 'ultimos_28_dias':
            fechaInicio = subDays(hoy, 27);
            fechaFin = endOfDay(hoy);
            tituloPeriodo = `Últimos 28 días (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(hoy, 'dd/MM/yyyy')})`;
            break;
            
          case 'ultimos_30_dias':
            fechaInicio = subDays(hoy, 29);
            fechaFin = endOfDay(hoy);
            tituloPeriodo = `Últimos 30 días (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(hoy, 'dd/MM/yyyy')})`;
            break;
            
          case 'ultimos_90_dias':
            fechaInicio = subDays(hoy, 89);
            fechaFin = endOfDay(hoy);
            tituloPeriodo = `Últimos 90 días (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(hoy, 'dd/MM/yyyy')})`;
            break;
            
          case 'ultimos_12_meses':
            fechaInicio = subMonths(hoy, 11);
            fechaFin = endOfDay(hoy);
            tituloPeriodo = `Últimos 12 meses (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(hoy, 'dd/MM/yyyy')})`;
            break;
            
          case 'anio_anterior':
            fechaInicio = startOfYear(subYears(hoy, 1));
            fechaFin = endOfYear(subYears(hoy, 1));
            tituloPeriodo = `Año calendario anterior (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(fechaFin, 'dd/MM/yyyy')})`;
            break;
            
          case 'este_anio':
            fechaInicio = startOfYear(hoy);
            fechaFin = endOfDay(hoy);
            tituloPeriodo = `Este año (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(hoy, 'dd/MM/yyyy')})`;
            break;
            
          case 'mes_pasado':
            fechaInicio = startOfMonth(subMonths(hoy, 1));
            fechaFin = endOfMonth(subMonths(hoy, 1));
            tituloPeriodo = `Mes pasado (${format(fechaInicio, 'dd/MM/yyyy')} - ${format(fechaFin, 'dd/MM/yyyy')})`;
            break;
            
          default:
            setError('Periodo no válido');
            setIsLoading(false);
            return;
        }
      }
      
      // Guardar el título del periodo para mostrarlo en la UI
      setPeriodoMostrado(tituloPeriodo);
      
      const filtros = {
        fechaInicio: format(fechaInicio, 'yyyy-MM-dd'),
        fechaFin: format(fechaFin, 'yyyy-MM-dd'),
        limit: 9999
      };
      
      if (medio && medio !== '') {
        filtros.medio = medio;
      }
      
      console.log('Consultando datos con filtros:', filtros);
      
      const response = await getAvisos(filtros);
      
      if (response.success) {
        console.log(`Recibidos ${response.data.length} avisos del servidor`);
        procesarDatosAvisos(response.data, fechaInicio, fechaFin);
        
        // Verificar si hay una comparación seleccionada y cargarla
        if (comparacionSeleccionada) {
          console.log('Cargando datos de comparación...');
          // Esperar a que se complete la carga de comparación antes de continuar
          await cargarDatosComparacion(periodo);
          // Solo activamos el modo comparación si se cargaron los datos correctamente
          if (datosComparacion) {
            setModoComparacion(true);
          }
        }
      } else {
        setError('No se pudieron cargar los datos de avisos.');
      }
    } catch (error) {
      console.error('Error al cargar datos del periodo:', error);
      setError('Error de conexión. Por favor, verifique su conexión e intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Función para cargar datos de comparación
  const cargarDatosComparacion = async (periodoActual) => {
    // Evitar múltiples llamadas simultáneas
    if (loadingComparacion) {
      console.log('Ya hay una carga de comparación en proceso, esperando...');
      return;
    }
    
    setLoadingComparacion(true);
    
    try {
      let filtrosComparacion = {};
      let tituloPeriodoComparacion = '';
      let fechaInicioComp, fechaFinComp;
      
      // Obtener las fechas del periodo actual
      let fechaInicioActual, fechaFinActual;
      
      // Calcular las fechas del periodo actual según el periodo seleccionado
      const hoy = new Date();
      const ayer = subDays(hoy, 1);
      const inicioSemanaActual = startOfWeek(hoy, { weekStartsOn: 1 }); // Lunes
      const inicioSemanaPasada = subWeeks(inicioSemanaActual, 1);
      const finSemanaPasada = subDays(inicioSemanaActual, 1); // Domingo anterior
      
      switch(periodoActual) {
        case 'mes_actual':
          fechaInicioActual = startOfMonth(hoy);
          fechaFinActual = endOfMonth(hoy);
          break;
          
        case 'hoy':
          fechaInicioActual = startOfDay(hoy);
          fechaFinActual = endOfDay(hoy);
          break;
          
        case 'ayer':
          fechaInicioActual = startOfDay(ayer);
          fechaFinActual = endOfDay(ayer);
          break;
          
        case 'esta_semana':
          fechaInicioActual = inicioSemanaActual;
          fechaFinActual = endOfDay(hoy);
          break;
          
        case 'semana_pasada':
          fechaInicioActual = inicioSemanaPasada;
          fechaFinActual = finSemanaPasada;
          break;
          
        case 'ultimos_7_dias':
          fechaInicioActual = subDays(hoy, 6);
          fechaFinActual = endOfDay(hoy);
          break;
          
        case 'ultimos_28_dias':
          fechaInicioActual = subDays(hoy, 27);
          fechaFinActual = endOfDay(hoy);
          break;
          
        case 'ultimos_30_dias':
          fechaInicioActual = subDays(hoy, 29);
          fechaFinActual = endOfDay(hoy);
          break;
          
        case 'ultimos_90_dias':
          fechaInicioActual = subDays(hoy, 89);
          fechaFinActual = endOfDay(hoy);
          break;
          
        case 'ultimos_12_meses':
          fechaInicioActual = subMonths(hoy, 11);
          fechaFinActual = endOfDay(hoy);
          break;
          
        case 'anio_anterior':
          fechaInicioActual = startOfYear(subYears(hoy, 1));
          fechaFinActual = endOfYear(subYears(hoy, 1));
          break;
          
        case 'este_anio':
          fechaInicioActual = startOfYear(hoy);
          fechaFinActual = endOfDay(hoy);
          break;
          
        case 'mes_pasado':
          fechaInicioActual = startOfMonth(subMonths(hoy, 1));
          fechaFinActual = endOfMonth(subMonths(hoy, 1));
          break;
          
        case 'personalizado':
          if (fechaInicioPersonalizada && fechaFinPersonalizada) {
            fechaInicioActual = parse(fechaInicioPersonalizada, 'yyyy-MM-dd', new Date());
            fechaFinActual = parse(fechaFinPersonalizada, 'yyyy-MM-dd', new Date());
          } else {
            setError('Para comparación personalizada, seleccione las fechas en el selector de periodo personalizado');
            setLoadingComparacion(false);
            return;
          }
          break;
          
        default:
          setError('Periodo no válido para comparación');
          setLoadingComparacion(false);
          return;
      }
      
      // Calcular la duración del periodo actual
      const duracionPeriodo = differenceInDays(fechaFinActual, fechaInicioActual) + 1;
      
      // Determinar el periodo de comparación
      if (comparacionSeleccionada === 'periodo_anterior_semana') {
        // Para coincidir con el día de la semana, calculamos hacia atrás la misma duración
        fechaFinComp = subDays(fechaInicioActual, 1);
        fechaInicioComp = subDays(fechaFinComp, duracionPeriodo - 1);
        
        filtrosComparacion = {
          fechaInicio: format(fechaInicioComp, 'yyyy-MM-dd'),
          fechaFin: format(fechaFinComp, 'yyyy-MM-dd'),
          limit: 9999
        };
        
        tituloPeriodoComparacion = `Periodo anterior (${format(fechaInicioComp, 'dd/MM/yyyy')} - ${format(fechaFinComp, 'dd/MM/yyyy')})`;
      } else if (comparacionSeleccionada === 'anio_anterior_semana') {
        // Para el mismo periodo del año anterior, manteniendo el día de la semana
        fechaInicioComp = new Date(fechaInicioActual);
        fechaInicioComp.setFullYear(fechaInicioComp.getFullYear() - 1);
        
        fechaFinComp = new Date(fechaFinActual);
        fechaFinComp.setFullYear(fechaFinComp.getFullYear() - 1);
        
        filtrosComparacion = {
          fechaInicio: format(fechaInicioComp, 'yyyy-MM-dd'),
          fechaFin: format(fechaFinComp, 'yyyy-MM-dd'),
          limit: 9999
        };
        
        tituloPeriodoComparacion = `Mismo período del año pasado (${format(fechaInicioComp, 'dd/MM/yyyy')} - ${format(fechaFinComp, 'dd/MM/yyyy')})`;
      } else if (comparacionSeleccionada === 'periodo_anterior') {
        // Periodo anterior simple
        fechaFinComp = subDays(fechaInicioActual, 1);
        fechaInicioComp = subDays(fechaFinComp, duracionPeriodo - 1);
        
        filtrosComparacion = {
          fechaInicio: format(fechaInicioComp, 'yyyy-MM-dd'),
          fechaFin: format(fechaFinComp, 'yyyy-MM-dd'),
          limit: 9999
        };
        
        tituloPeriodoComparacion = `Periodo anterior (${format(fechaInicioComp, 'dd/MM/yyyy')} - ${format(fechaFinComp, 'dd/MM/yyyy')})`;
      } else if (comparacionSeleccionada === 'anio_anterior') {
        // Mismo periodo del año anterior
        fechaInicioComp = new Date(fechaInicioActual);
        fechaInicioComp.setFullYear(fechaInicioComp.getFullYear() - 1);
        
        fechaFinComp = new Date(fechaFinActual);
        fechaFinComp.setFullYear(fechaFinComp.getFullYear() - 1);
        
        filtrosComparacion = {
          fechaInicio: format(fechaInicioComp, 'yyyy-MM-dd'),
          fechaFin: format(fechaFinComp, 'yyyy-MM-dd'),
          limit: 9999
        };
        
        tituloPeriodoComparacion = `Mismo período del año pasado (${format(fechaInicioComp, 'dd/MM/yyyy')} - ${format(fechaFinComp, 'dd/MM/yyyy')})`;
      } else if (comparacionSeleccionada === 'personalizado') {
        // Para periodo personalizado, necesitamos que el usuario seleccione las fechas
        setError('Para comparación personalizada, seleccione las fechas en el selector de periodo personalizado');
        setLoadingComparacion(false);
        return;
      }
      
      // Agregar el filtro de medio si está seleccionado
      if (medioSeleccionado && medioSeleccionado !== '') {
        filtrosComparacion.medio = medioSeleccionado;
      }
      
      console.log('Cargando datos de comparación con filtros:', filtrosComparacion);
      
      // Cargar los datos de comparación
      const responseComparacion = await getAvisos(filtrosComparacion);
      
      if (responseComparacion.success) {
        console.log(`Recibidos ${responseComparacion.data.length} avisos de comparación`);
        
        // Procesar los datos de comparación
        const datosComp = procesarDatosParaComparacion(responseComparacion.data);
        
        // Guardar los datos procesados junto con la información del periodo
        setDatosComparacion({
          datos: datosComp,
          periodo: tituloPeriodoComparacion,
          totalValor: datosComp.totalValor || 0,
          totalAvisos: datosComp.totalAvisos || 0,
          fechaInicio: fechaInicioComp,
          fechaFin: fechaFinComp,
          titulo: tituloPeriodoComparacion
        });
      } else {
        setError('No se pudieron cargar los datos para comparación');
        setDatosComparacion(null);
      }
    } catch (error) {
      console.error('Error al cargar datos de comparación:', error);
      setError('No se pudieron cargar los datos para comparación');
      setDatosComparacion(null);
    } finally {
      setLoadingComparacion(false);
    }
  };
  
  // Función para calcular la variación entre dos valores
  const calcularVariacion = (actual, anterior) => {
    if (!anterior || anterior === 0) return { valor: 0, porcentaje: 0 };
    
    const diferencia = actual - anterior;
    const porcentaje = (diferencia / anterior) * 100;
    
    return {
      valor: diferencia,
      porcentaje: porcentaje
    };
  };
  
  // Función para procesar datos de avisos
  const procesarDatosAvisos = (avisos, fechaInicio, fechaFin) => {
    if (!avisos || avisos.length === 0) {
      setError('No se encontraron datos para el periodo seleccionado.');
      return;
    }
    
    console.log(`Procesando ${avisos.length} avisos...`);
    console.log('Rango de fechas:', format(fechaInicio, 'dd/MM/yyyy'), 'a', format(fechaFin, 'dd/MM/yyyy'));
    
    // Crear una copia para no modificar los originales
    const avisosProcesados = [...avisos];
    
    // Datos generales - procesamos directamente sin filtrar por fecha en el cliente
    // para mantener consistencia con lo que muestra el reporte de avisos
    const totalAvisos = avisosProcesados.length;
    
    // Calcular el valor total correcto, asegurando que se conviertan a números
    const totalValor = avisosProcesados.reduce((sum, aviso) => {
      const valor = parseFloat(aviso.valor_con_descuento || 0);
      if (isNaN(valor)) {
        console.warn(`Valor no numérico encontrado: ${aviso.valor_con_descuento} para aviso ${aviso.nid}`);
        return sum;
      }
      return sum + valor;
    }, 0);
    
    console.log(`Total calculado: ${totalValor.toLocaleString('es-CL')}`);
    
    // Mostrar algunos avisos con valores altos para verificación
    const avisosOrdenados = [...avisosProcesados].sort((a, b) => 
      parseFloat(b.valor_con_descuento || 0) - parseFloat(a.valor_con_descuento || 0)
    );
    
    console.log('Top 5 avisos con mayor valor:');
    avisosOrdenados.slice(0, 5).forEach((aviso, idx) => {
      console.log(`${idx + 1}. NID: ${aviso.nid}, Valor: ${aviso.valor_con_descuento}, Fecha: ${aviso.fecha_publicacion}`);
    });
    
    // Calcular estados
    const publicados = avisosProcesados.filter(aviso => 
      aviso.estado_aviso_impreso === 'Publicado' || 
      aviso.estado_aviso_impreso === 'Estado 71'
    ).length;
    
    const pendientes = avisosProcesados.filter(aviso => 
      aviso.estado_aviso_impreso === 'Pendiente' || 
      aviso.estado_aviso_impreso === 'Estado 68'
    ).length;
    
    // Calcular días en el período
    const totalDiasEnPeriodo = differenceInDays(fechaFin, fechaInicio) + 1;
    console.log(`Días en el período: ${totalDiasEnPeriodo}`);
    
    // Calcular promedio de avisos por día
    const promedioAvisosPorDia = totalAvisos / totalDiasEnPeriodo;
    console.log(`Promedio de avisos por día: ${promedioAvisosPorDia.toFixed(1)}`);
    
    setDatosAvisos({
      totalAvisos,
      totalValor,
      publicados,
      pendientes,
      promedioValor: totalAvisos > 0 ? totalValor / totalAvisos : 0,
      diasEnPeriodo: totalDiasEnPeriodo, // Añadimos el número de días al objeto de datos
      promedioAvisosPorDia // Añadimos el promedio de avisos por día
    });
    
    // Top ejecutivos por valor vendido
    const ejecutivosPorValor = {};
    avisosProcesados.forEach(aviso => {
      if (!aviso.ejecutivo) return;
      
      const ejecutivo = aviso.ejecutivo;
      const valor = parseFloat(aviso.valor_con_descuento || 0);
      
      if (ejecutivosPorValor[ejecutivo]) {
        ejecutivosPorValor[ejecutivo].valor += valor;
        ejecutivosPorValor[ejecutivo].avisos += 1;
      } else {
        ejecutivosPorValor[ejecutivo] = {
          nombre: ejecutivo,
          valor: valor,
          avisos: 1
        };
      }
    });
    
    const topEjecutivosArray = Object.values(ejecutivosPorValor)
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 10);
    
    setTopEjecutivos(topEjecutivosArray);
    
    // Top clientes por valor
    const clientesPorValor = {};
    avisosProcesados.forEach(aviso => {
      if (!aviso.empresa_cliente) return;
      
      const cliente = aviso.empresa_cliente;
      const valor = parseFloat(aviso.valor_con_descuento || 0);
      
      if (clientesPorValor[cliente]) {
        clientesPorValor[cliente].valor += valor;
        clientesPorValor[cliente].avisos += 1;
      } else {
        clientesPorValor[cliente] = {
          nombre: cliente,
          valor: valor,
          avisos: 1
        };
      }
    });
    
    // Convertir a array para ordenar de diferentes maneras
    const todosClientesArray = Object.values(clientesPorValor);
    
    // Para el gráfico, usamos los top 10 por valor
    const topClientesArray = [...todosClientesArray]
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 10);
    
    setTopClientes(todosClientesArray);
    
    // Distribución por estados
    setDistribucionEstados([
      { estado: 'Publicados', count: publicados },
      { estado: 'Pendientes', count: pendientes }
    ]);
    
    // Tendencia de ventas por día
    const ventasPorDia = {};
    console.log('Procesando avisos para tendencias, total avisos:', avisosProcesados.length);
    
    // Verificar primero si hay datos para procesar
    if (avisosProcesados.length === 0) {
      console.log('No hay avisos para procesar en tendencias');
      setTendenciaVentas([]);
      return;
    }
    
    // Primero crear un conjunto de fechas entre fechaInicio y fechaFin
    let fechaTemp = new Date(fechaInicio);
    while (fechaTemp <= fechaFin) {
      const fechaKey = format(fechaTemp, 'yyyy-MM-dd');
      ventasPorDia[fechaKey] = {
        fecha: new Date(fechaTemp),
        dia: format(fechaTemp, 'dd/MM/yyyy'),
        valor: 0,
        avisos: 0
      };
      fechaTemp = addDays(fechaTemp, 1);
    }
    
    // Mostrar el primer aviso para diagnóstico
    if (avisosProcesados.length > 0) {
      const muestraAviso = avisosProcesados[0];
      console.log('Ejemplo de aviso para diagnóstico:');
      console.log(muestraAviso);
      console.log('Tipo de fecha_publicacion:', typeof muestraAviso.fecha_publicacion);
      console.log('Tipo de valor_con_descuento:', typeof muestraAviso.valor_con_descuento);
    }
    
    // Ahora procesar los avisos
    let avisosConError = 0;
    let avisosProcesadosOK = 0;
    
    avisosProcesados.forEach((aviso, index) => {
      try {
        // Extraer valor y asegurar que sea un número
        const valorStr = aviso.valor_con_descuento || '0';
        // Eliminar caracteres no numéricos excepto el punto decimal
        const valorLimpio = valorStr.toString().replace(/[^\d.-]/g, '');
        const valor = parseFloat(valorLimpio);
        
        if (isNaN(valor)) {
          console.log(`Error en valor del aviso ${index}:`, aviso.nid, valorStr);
          avisosConError++;
          return;
        }

        // Convertir la fecha de publicación a objeto Date
        let fecha;
        const fechaStr = aviso.fecha_publicacion;
        
        if (!fechaStr) {
          console.log(`Aviso ${index} sin fecha:`, aviso.nid);
          avisosConError++;
          return;
        }
        
        // Según el formato que viene de avisosController.js (DD/MM/YYYY o DD-MM-YYYY)
        if (typeof fechaStr === 'string') {
          // Intentar varios formatos de fecha
          if (fechaStr.includes('/')) {
            const partesFecha = fechaStr.split('/');
            if (partesFecha.length !== 3) {
              console.log(`Formato fecha incorrecto ${index}:`, fechaStr);
              avisosConError++;
              return;
            }
            
            const dia = parseInt(partesFecha[0], 10);
            const mes = parseInt(partesFecha[1], 10) - 1; // Meses en JS son 0-11
            const anio = parseInt(partesFecha[2], 10);
            
            if (isNaN(dia) || isNaN(mes) || isNaN(anio)) {
              console.log(`Valores fecha inválidos ${index}:`, dia, mes, anio);
              avisosConError++;
              return;
            }
            
            fecha = new Date(anio, mes, dia);
          } else if (fechaStr.includes('-')) {
            // Formato DD-MM-YYYY
            const partesFecha = fechaStr.split('-');
            if (partesFecha.length !== 3) {
              console.log(`Formato fecha con guiones incorrecto ${index}:`, fechaStr);
              avisosConError++;
              return;
            }
            
            // Primero identificar si es formato DD-MM-YYYY o YYYY-MM-DD
            let dia, mes, anio;
            
            // Verificar si el primer componente tiene 4 dígitos (probablemente año - formato ISO)
            if (partesFecha[0].length === 4) {
              // Formato YYYY-MM-DD (ISO)
              anio = parseInt(partesFecha[0], 10);
              mes = parseInt(partesFecha[1], 10) - 1;
              dia = parseInt(partesFecha[2], 10);
            } else {
              // Formato DD-MM-YYYY (estilo europeo/latinoamericano)
              dia = parseInt(partesFecha[0], 10);
              mes = parseInt(partesFecha[1], 10) - 1;
              anio = parseInt(partesFecha[2], 10);
            }
            
            if (isNaN(dia) || isNaN(mes) || isNaN(anio)) {
              console.log(`Valores fecha con guiones inválidos ${index}:`, dia, mes, anio);
              avisosConError++;
              return;
            }
            
            fecha = new Date(anio, mes, dia);
            
            // Verificación adicional para debugging
            if (isNaN(fecha.getTime())) {
              console.log(`Conversión de fecha con guiones fallida ${index}:`, { 
                original: fechaStr, 
                dia: dia, 
                mes: mes, 
                anio: anio,
                resultado: fecha
              });
              avisosConError++;
              return;
            }
          } else {
            console.log(`Formato fecha desconocido ${index}:`, fechaStr);
            avisosConError++;
            return;
          }
        } else {
          console.log(`Tipo de fecha inválido ${index}:`, typeof fechaStr);
          avisosConError++;
          return;
        }
        
        // Verificar que la fecha sea válida
        if (isNaN(fecha.getTime())) {
          console.log(`Fecha inválida ${index}:`, fechaStr);
          avisosConError++;
          return;
        }
        
        // Verificar si la fecha está dentro del rango seleccionado
        if (fecha < fechaInicio || fecha > fechaFin) {
          // No contar como error, solo ignorar
          return;
        }
        
        // Usar formato ISO para la fecha (YYYY-MM-DD)
        const diaKey = format(fecha, 'yyyy-MM-dd');
        
        if (ventasPorDia[diaKey]) {
          ventasPorDia[diaKey].valor += valor;
          ventasPorDia[diaKey].avisos += 1;
          avisosProcesadosOK++;
        } else {
          console.log(`Fecha no encontrada en diccionario ${index}:`, diaKey);
          // Crear el día si no existe por alguna razón
          ventasPorDia[diaKey] = {
            fecha: fecha,
            dia: format(fecha, 'dd/MM/yyyy'),
            valor: valor,
            avisos: 1
          };
          avisosProcesadosOK++;
        }
      } catch (error) {
        console.error(`Error al procesar aviso ${index}:`, error);
        if (index < 5) {
          console.log('Datos del aviso con error:', aviso);
        }
        avisosConError++;
      }
    });
    
    // Convertir a array y ordenar por fecha
    const ventasPorDiaArray = Object.values(ventasPorDia)
      .sort((a, b) => a.fecha - b.fecha);
    
    console.log(`Tendencia generada, días: ${ventasPorDiaArray.length}, avisos procesados: ${avisosProcesadosOK}, errores: ${avisosConError}`);
    
    // Optimizar la granularidad según la extensión del período
    const diasParaGranularidad = differenceInDays(fechaFin, fechaInicio) + 1;
    let ventasTendencia = [];
    let granularidad = '';
    
    if (diasParaGranularidad <= 31) {
      // Para períodos cortos (hasta 31 días) -> granularidad diaria
      granularidad = 'diaria';
      ventasTendencia = ventasPorDiaArray.map(dia => ({
        ...dia,
        esDomingo: isSunday(dia.fecha),
        esInicioMes: isFirstDayOfMonth(dia.fecha)
      }));
    } else if (diasParaGranularidad <= 90) {
      // Para períodos medianos (hasta 3 meses) -> granularidad semanal
      granularidad = 'semanal';
      const semanas = {};
      
      ventasPorDiaArray.forEach(dia => {
        const fecha = new Date(dia.fecha);
        const inicioSemana = startOfWeek(fecha, { weekStartsOn: 1 }); // Semana comienza el lunes
        const finSemana = endOfWeek(fecha, { weekStartsOn: 1 });
        const semanaKey = `${getISOWeekYear(fecha)}-W${getISOWeek(fecha)}`;
        const label = `Sem ${getISOWeek(fecha)} (${format(inicioSemana, 'dd/MM')} - ${format(finSemana, 'dd/MM')})`;
        
        if (semanas[semanaKey]) {
          semanas[semanaKey].valor += dia.valor;
          semanas[semanaKey].avisos += dia.avisos;
          semanas[semanaKey].dias.push(dia);
        } else {
          semanas[semanaKey] = {
            fecha: inicioSemana,
            dia: label,
            valor: dia.valor,
            avisos: dia.avisos,
            semana: getISOWeek(fecha),
            año: getISOWeekYear(fecha),
            dias: [dia],
            contieneFinDeSemana: [5, 6, 0].some(d => d === fecha.getDay()) // viernes, sábado o domingo
          };
        }
      });
      
      ventasTendencia = Object.values(semanas).sort((a, b) => a.fecha - b.fecha);
    } else {
      // Para períodos largos (más de 3 meses) -> granularidad mensual
      granularidad = 'mensual';
      const mesesObj = {};
      
      ventasPorDiaArray.forEach(dia => {
        const fecha = new Date(dia.fecha);
        const año = fecha.getFullYear();
        const mes = fecha.getMonth();
        const mesKey = `${año}-${mes}`;
        const nombreMes = meses[mes];
        
        if (mesesObj[mesKey]) {
          mesesObj[mesKey].valor += dia.valor;
          mesesObj[mesKey].avisos += dia.avisos;
          mesesObj[mesKey].dias.push(dia);
        } else {
          mesesObj[mesKey] = {
            fecha: new Date(año, mes, 1),
            dia: `${nombreMes} ${año}`,
            valor: dia.valor,
            avisos: dia.avisos,
            mes: mes,
            año: año,
            dias: [dia]
          };
        }
      });
      
      ventasTendencia = Object.values(mesesObj).sort((a, b) => a.fecha - b.fecha);
    }
    
    console.log(`Granularidad aplicada: ${granularidad}. Puntos en el gráfico: ${ventasTendencia.length}`);
    setTendenciaVentas(ventasTendencia);
    
    // Rendimiento por medio
    const medioPorValor = {};
    avisosProcesados.forEach(aviso => {
      if (!aviso.empresa_proveedora) return;
      
      const medio = aviso.empresa_proveedora;
      const valor = parseFloat(aviso.valor_con_descuento || 0);
      
      if (medioPorValor[medio]) {
        medioPorValor[medio].valor += valor;
        medioPorValor[medio].avisos += 1;
      } else {
        medioPorValor[medio] = {
          nombre: medio,
          valor: valor,
          avisos: 1
        };
      }
    });
    
    const rendimientoMedioArray = Object.values(medioPorValor)
      .sort((a, b) => b.valor - a.valor);
    
    setRendimientoMedio(rendimientoMedioArray);
  };
  
  // Función para procesar datos para comparación
  const procesarDatosParaComparacion = (avisos) => {
    if (!avisos || avisos.length === 0) {
      console.log('No hay datos para la comparación');
      return { totalAvisos: 0, totalValor: 0 };
    }
    
    console.log(`Procesando ${avisos.length} avisos para comparación...`);
    
    // Calcular el valor total correcto para comparación
    const totalValor = avisos.reduce((sum, aviso) => {
      const valor = parseFloat(aviso.valor_con_descuento || 0);
      if (isNaN(valor)) {
        console.warn(`Valor no numérico encontrado en comparación: ${aviso.valor_con_descuento} para aviso ${aviso.nid}`);
        return sum;
      }
      return sum + valor;
    }, 0);
    
    console.log(`Total calculado para comparación: ${totalValor.toLocaleString('es-CL')}`);
    
    // Calcular estados
    const publicados = avisos.filter(aviso => 
      aviso.estado_aviso_impreso === 'Publicado' || 
      aviso.estado_aviso_impreso === 'Estado 71'
    ).length;
    
    const pendientes = avisos.filter(aviso => 
      aviso.estado_aviso_impreso === 'Pendiente' || 
      aviso.estado_aviso_impreso === 'Estado 68'
    ).length;
    
    return {
      totalAvisos: avisos.length,
      totalValor: totalValor,
      publicados: publicados,
      pendientes: pendientes,
      avisos: avisos
    };
  };
  
  // Función para formatear valores monetarios
  const formatearMoneda = (valor) => {
    if (valor === undefined || valor === null) return '$0';
    return `$${Math.round(valor).toLocaleString('es-CL')}`;
  };
  
  // Preparar datos para gráficos
  const datosGraficoTendencia = {
    labels: tendenciaVentas.map(item => item.dia),
    datasets: [
      {
        label: 'Ventas',
        data: tendenciaVentas.map(item => item.valor),
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderWidth: 2,
        tension: 0.3
      },
      {
        label: 'Cantidad de Avisos',
        data: tendenciaVentas.map(item => item.avisos),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderWidth: 2,
        tension: 0.3,
        yAxisID: 'y1'
      },
      // Agregar datos de comparación si están disponibles
      ...(datosComparacion ? [{
        label: `Ventas (${datosComparacion.periodo || 'Periodo anterior'})`,
        data: (() => {
          // Crear un mapa para las ventas del periodo de comparación
          const ventasPorDiaComp = {};
          
          // Asegurarnos de que tenemos los datos necesarios para hacer la comparación
          if (!datosComparacion.datos || !datosComparacion.datos.avisos) {
            console.warn('No hay datos suficientes para la comparación');
            return tendenciaVentas.map(() => 0);
          }
          
          // Procesar cada aviso del periodo de comparación
          datosComparacion.datos.avisos.forEach(aviso => {
            try {
              // Extraer el valor numérico
              const valor = parseFloat(aviso.valor_con_descuento || 0);
              if (isNaN(valor)) return;
              
              // Extraer y formatear la fecha
              const fechaStr = aviso.fecha_publicacion;
              if (!fechaStr) return;
              
              // Parsear la fecha según su formato
              let fecha;
              if (fechaStr.includes('/')) {
                const [dia, mes, anio] = fechaStr.split('/').map(Number);
                fecha = new Date(anio, mes - 1, dia);
              } else if (fechaStr.includes('-')) {
                if (fechaStr.charAt(4) === '-') {
                  // Formato YYYY-MM-DD
                  const [anio, mes, dia] = fechaStr.split('-').map(Number);
                  fecha = new Date(anio, mes - 1, dia);
                } else {
                  // Formato DD-MM-YYYY
                  const [dia, mes, anio] = fechaStr.split('-').map(Number);
                  fecha = new Date(anio, mes - 1, dia);
                }
              } else {
                return;
              }
              
              // Verificar fecha válida
              if (isNaN(fecha.getTime())) return;
              
              // Usar directamente la fecha original del aviso para acumular en el período de comparación
              const diaKey = format(fecha, 'yyyy-MM-dd');
              
              // Acumular valores solo si la fecha está dentro del periodo de comparación
              if (fecha >= datosComparacion.fechaInicio && fecha <= datosComparacion.fechaFin) {
                if (ventasPorDiaComp[diaKey] !== undefined) {
                  ventasPorDiaComp[diaKey] += valor;
                } else {
                  ventasPorDiaComp[diaKey] = valor;
                }
              }
            } catch (error) {
              console.error('Error procesando aviso para comparación', error);
            }
          });
          
          console.log('Ventas por día comparación procesadas:', Object.keys(ventasPorDiaComp).length, 'días');
          
          // Convertir a array y ordenar por fecha
          const ventasPorDiaCompArray = Object.entries(ventasPorDiaComp).map(([fecha, valor]) => ({
            fecha: new Date(fecha),
            dia: format(new Date(fecha), 'dd/MM/yyyy'),
            valor: valor,
            avisos: 0 // No necesitamos contar avisos para la comparación
          })).sort((a, b) => a.fecha - b.fecha);
          
          // Aplicar la misma granularidad que se usó para los datos actuales
          // Determinar qué granularidad se utilizó para el período actual
          const granularidadActual = tendenciaVentas.length > 0 && tendenciaVentas[0].dias ? 
            (tendenciaVentas[0].semana !== undefined ? 'semanal' : 'mensual') : 'diaria';
          
          console.log(`Aplicando granularidad ${granularidadActual} a datos de comparación`);
          
          let ventasCompTendencia = [];
          
          if (granularidadActual === 'diaria') {
            // Para granularidad diaria, usamos directamente los datos diarios
            ventasCompTendencia = ventasPorDiaCompArray;
          } else if (granularidadActual === 'semanal') {
            // Para granularidad semanal, agrupamos por semanas
            const semanasComp = {};
            
            ventasPorDiaCompArray.forEach(dia => {
              const fecha = new Date(dia.fecha);
              const inicioSemana = startOfWeek(fecha, { weekStartsOn: 1 });
              const semanaKey = `${getISOWeekYear(fecha)}-W${getISOWeek(fecha)}`;
              
              if (semanasComp[semanaKey]) {
                semanasComp[semanaKey].valor += dia.valor;
              } else {
                semanasComp[semanaKey] = {
                  fecha: inicioSemana,
                  semana: getISOWeek(fecha),
                  año: getISOWeekYear(fecha),
                  valor: dia.valor
                };
              }
            });
            
            ventasCompTendencia = Object.values(semanasComp).sort((a, b) => a.fecha - b.fecha);
          } else if (granularidadActual === 'mensual') {
            // Para granularidad mensual, agrupamos por meses
            const mesesCompObj = {};
            
            ventasPorDiaCompArray.forEach(dia => {
              const fecha = new Date(dia.fecha);
              const año = fecha.getFullYear();
              const mes = fecha.getMonth();
              const mesKey = `${año}-${mes}`;
              
              if (mesesCompObj[mesKey]) {
                mesesCompObj[mesKey].valor += dia.valor;
              } else {
                mesesCompObj[mesKey] = {
                  fecha: new Date(año, mes, 1),
                  mes: mes,
                  año: año,
                  valor: dia.valor
                };
              }
            });
            
            ventasCompTendencia = Object.values(mesesCompObj).sort((a, b) => a.fecha - b.fecha);
          }
          
          // Ahora hay que mapear los puntos de comparación a los del período actual
          return tendenciaVentas.map((itemActual, index) => {
            // Si no hay datos de comparación, devolvemos 0
            if (ventasCompTendencia.length === 0) return 0;
            
            // Para alinear correctamente, usamos el índice relativo
            // (mismo enfoque proporcional, pero con la misma granularidad)
            const porcentajeAvance = tendenciaVentas.length > 1 ? index / (tendenciaVentas.length - 1) : 0;
            const indexEnComparacion = Math.min(
              Math.floor(porcentajeAvance * ventasCompTendencia.length), 
              ventasCompTendencia.length - 1
            );
            
            // Obtenemos el valor correspondiente en la comparación
            return ventasCompTendencia[indexEnComparacion]?.valor || 0;
          });
        })(),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 2,
        borderDash: [10, 5],
        tension: 0.3,
        pointStyle: 'dash'
      }] : [])
    ]
  };
  
  const opcionesTendencia = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Fecha'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          callback: function(index) {
            // Personalización del estilo de las etiquetas
            const item = tendenciaVentas[index];
            if (!item) return '';
            
            // Si es granularidad diaria, resaltar domingos y primeros días del mes
            if (item.esDomingo) {
              return [`${item.dia}`, '(Dom)'];
            } else if (item.esInicioMes) {
              return [`${item.dia}`, '(Inicio)'];
            }
            return item.dia;
          },
          font: function(context) {
            const item = tendenciaVentas[context.index];
            if (!item) return {};
            
            // Aplicar negrita a los domingos
            if (item.esDomingo || item.contieneFinDeSemana) {
              return {
                weight: 'bold'
              };
            }
            return {};
          }
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Ventas ($)'
        },
        beginAtZero: true
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Cantidad de Avisos'
        },
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: function(context) {
            const item = tendenciaVentas[context[0].dataIndex];
            if (!item) return '';
            
            // Mostrar información adicional en los tooltips según la granularidad
            if (item.dias && item.dias.length > 0) {
              const primerDia = item.dias[0].dia;
              const ultimoDia = item.dias[item.dias.length - 1].dia;
              return `${item.dia} (${item.dias.length} días: ${primerDia} - ${ultimoDia})`;
            }
            return item.dia + (item.esDomingo ? ' (Domingo)' : '');
          },
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            
            // Para datos de ventas, formatear como moneda
            if (context.dataset.label.includes('Ventas')) {
              if (context.parsed.y !== null) {
                label += formatearMoneda(context.parsed.y);
              }
            } else {
              // Para datos de cantidad de avisos
              if (context.parsed.y !== null) {
                label += context.parsed.y.toLocaleString('es-CL');
              }
            }
            
            return label;
          }
        }
      },
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      // Resaltar mejor los datos de comparación
      ...(datosComparacion ? {
        title: {
          display: true,
          text: 'Comparación: ' + (datosComparacion.periodo || 'Periodo anterior'),
          font: {
            size: 14
          }
        }
      } : {})
    }
  };
  
  const datosGraficoMedios = {
    labels: rendimientoMedio.map(item => item.nombre),
    datasets: [
      {
        label: 'Ventas por Medio',
        data: rendimientoMedio.map(item => item.valor),
        backgroundColor: colores,
        borderWidth: 1,
      },
      // Mostrar datos de comparación si están disponibles
      ...(datosComparacion ? [{
        label: 'Ventas por Medio (Periodo Anterior)',
        data: rendimientoMedio.map(item => {
          // Buscar el equivalente en datos de comparación (mock simplificado)
          return item.valor * (datosComparacion.totalValor / datosAvisos.totalValor);
        }),
        backgroundColor: rendimientoMedio.map((_, i) => colores[i] + '55'), // Mismos colores pero con transparencia
        borderWidth: 1,
        borderColor: '#000',
      }] : [])
    ]
  };
  
  const datosGraficoEstados = {
    labels: distribucionEstados.map(item => item.estado),
    datasets: [
      {
        label: 'Avisos por Estado',
        data: distribucionEstados.map(item => item.count),
        backgroundColor: ['rgba(40, 167, 69, 0.8)', 'rgba(255, 193, 7, 0.8)'],
        borderWidth: 1,
      },
      // Mostrar datos de comparación si están disponibles
      ...(datosComparacion ? [{
        label: 'Avisos por Estado (Periodo Anterior)',
        data: [
          datosComparacion.avisos?.filter(aviso => 
            aviso.estado_aviso_impreso === 'Publicado' || 
            aviso.estado_aviso_impreso === 'Estado 71'
          ).length || 0,
          datosComparacion.avisos?.filter(aviso => 
            aviso.estado_aviso_impreso === 'Pendiente' || 
            aviso.estado_aviso_impreso === 'Estado 68'
          ).length || 0
        ],
        backgroundColor: ['rgba(40, 167, 69, 0.4)', 'rgba(255, 193, 7, 0.4)'],
        borderWidth: 1,
        borderColor: ['rgba(40, 167, 69, 1)', 'rgba(255, 193, 7, 1)'],
      }] : [])
    ]
  };
  
  const datosGraficoEjecutivos = {
    labels: topEjecutivos.map(item => item.nombre),
    datasets: [
      {
        label: 'Ventas por Ejecutivo',
        data: topEjecutivos.map(item => item.valor),
        backgroundColor: colores,
        borderWidth: 1,
      },
      // Mostrar datos de comparación si están disponibles
      ...(datosComparacion && datosComparacion.avisos ? [{
        label: 'Ventas por Ejecutivo (Periodo Anterior)',
        data: topEjecutivos.map(ejecutivo => {
          // Crear un mapa de ejecutivos del periodo de comparación
          const ejecutivosPorValorComp = {};
          datosComparacion.avisos.forEach(aviso => {
            if (!aviso.ejecutivo) return;
            
            const ejecutivoComp = aviso.ejecutivo;
            const valor = parseFloat(aviso.valor_con_descuento || 0);
            
            if (ejecutivosPorValorComp[ejecutivoComp]) {
              ejecutivosPorValorComp[ejecutivoComp].valor += valor;
            } else {
              ejecutivosPorValorComp[ejecutivoComp] = { valor };
            }
          });
          
          // Devolver el valor del ejecutivo en el periodo de comparación, o 0 si no existe
          return ejecutivosPorValorComp[ejecutivo.nombre]?.valor || 0;
        }),
        backgroundColor: topEjecutivos.map((_, i) => colores[i] + '55'), // Mismos colores pero con transparencia
        borderWidth: 1,
        borderColor: '#000',
      }] : [])
    ]
  };
  
  const datosGraficoClientes = {
    labels: topClientes.slice(0, 10).map(item => 
      item.nombre.length > 20 ? item.nombre.substring(0, 17) + '...' : item.nombre
    ),
    datasets: [
      {
        label: 'Ventas por Cliente',
        data: topClientes.slice(0, 10).map(item => item.valor),
        backgroundColor: colores,
        borderWidth: 1,
      },
      // Mostrar datos de comparación si están disponibles
      ...(datosComparacion && datosComparacion.avisos ? [{
        label: 'Ventas por Cliente (Periodo Anterior)',
        data: topClientes.slice(0, 10).map(cliente => {
          // Crear un mapa de clientes del periodo de comparación
          const clientesPorValorComp = {};
          datosComparacion.avisos.forEach(aviso => {
            if (!aviso.empresa_cliente) return;
            
            const clienteComp = aviso.empresa_cliente;
            const valor = parseFloat(aviso.valor_con_descuento || 0);
            
            if (clientesPorValorComp[clienteComp]) {
              clientesPorValorComp[clienteComp].valor += valor;
            } else {
              clientesPorValorComp[clienteComp] = { valor };
            }
          });
          
          // Devolver el valor del cliente en el periodo de comparación, o 0 si no existe
          return clientesPorValorComp[cliente.nombre]?.valor || 0;
        }),
        backgroundColor: topClientes.slice(0, 10).map((_, i) => colores[i] + '55'), // Mismos colores pero con transparencia
        borderWidth: 1,
        borderColor: '#000',
      }] : [])
    ]
  };
  
  // Opciones comunes para gráficos
  const opcionesGraficos = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Distribución',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatearMoneda(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
  };
  
  // Opciones específicas para los gráficos de barras
  const opcionesGraficoBarras = {
    ...opcionesGraficos,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Ventas ($)'
        },
        ticks: {
          callback: function(value) {
            return formatearMoneda(value);
          }
        }
      }
    },
    plugins: {
      ...opcionesGraficos.plugins,
      ...(datosComparacion ? {
        title: {
          display: true,
          text: datosComparacion?.titulo ? 'Comparación con: ' + datosComparacion.titulo : 'Comparación con periodo anterior',
          font: {
            size: 14
          }
        }
      } : {})
    }
  };
  
  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">Dashboard Comercial - Diario El Día</h1>
          <p className="text-muted">
            Panel de control con indicadores clave de desempeño para la toma de decisiones
          </p>
        </Col>
      </Row>
      
      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>Periodo de análisis</Form.Label>
                <Form.Select 
                  value={periodoTemp}
                  onChange={(e) => {
                    const valor = e.target.value;
                    setPeriodoTemp(valor);
                    setMostrarPeriodoPersonalizado(valor === 'personalizado');
                  }}
                >
                  <option value="mes_actual">Mes actual</option>
                  <option value="hoy">Hoy</option>
                  <option value="ayer">Ayer</option>
                  <option value="esta_semana">Esta semana (Lunes hasta hoy)</option>
                  <option value="semana_pasada">Semana pasada (Lunes a Domingo)</option>
                  <option value="ultimos_7_dias">Últimos 7 días</option>
                  <option value="ultimos_28_dias">Últimos 28 días</option>
                  <option value="ultimos_30_dias">Últimos 30 días</option>
                  <option value="ultimos_90_dias">Últimos 90 días</option>
                  <option value="ultimos_12_meses">Últimos 12 meses</option>
                  <option value="anio_anterior">Año calendario anterior</option>
                  <option value="este_anio">Este año (Enero a hoy)</option>
                  <option value="mes_pasado">Mes pasado</option>
                  <option value="personalizado">Personalizado</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Medio (opcional)</Form.Label>
                <Form.Select 
                  value={medioSeleccionado}
                  onChange={(e) => {
                    setMedioSeleccionado(e.target.value);
                  }}
                >
                  <option value="">Todos los medios</option>
                  {mediosDisponibles.map(medio => (
                    <option key={medio.id} value={medio.id}>{medio.nombre}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Comparar con</Form.Label>
                <Form.Select 
                  value={comparacionSeleccionada}
                  onChange={(e) => {
                    const valor = e.target.value;
                    setComparacionSeleccionada(valor);
                    // No activamos el modo comparación aquí, solo actualizamos el estado
                  }}
                  className="form-select-sm"
                  disabled={loadingComparacion || isLoading}
                >
                  <option value="">Seleccione una comparación</option>
                  <option value="periodo_anterior_semana">Periodo anterior (coincidir día de la semana)</option>
                  <option value="anio_anterior_semana">Mismo período del año pasado (coincidir día de la semana)</option>
                  <option value="periodo_anterior">Periodo anterior</option>
                  <option value="anio_anterior">Mismo período del año pasado</option>
                  <option value="personalizado">Personalizado</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          {/* Selector de fechas para periodo personalizado */}
          {mostrarPeriodoPersonalizado && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Fecha inicio</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={fechaInicioPersonalizada}
                    onChange={(e) => setFechaInicioPersonalizada(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Fecha fin</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={fechaFinPersonalizada}
                    onChange={(e) => setFechaFinPersonalizada(e.target.value)}
                  />
                </Form.Group>
              </Col>
              {errorPeriodoPersonalizado && (
                <Col md={12}>
                  <Alert variant="danger" className="py-2 mt-2">{errorPeriodoPersonalizado}</Alert>
                </Col>
              )}
            </Row>
          )}
          
          <Row className="mt-2">
            <Col>
              <Button 
                variant="primary" 
                onClick={() => {
                  setPeriodoSeleccionado(periodoTemp);
                  cargarDatosPeriodo(periodoTemp, medioSeleccionado);
                }}
              >
                <i className="bi bi-search me-1"></i> Actualizar datos
              </Button>
              {isLoading && (
                <span className="ms-2 text-muted">
                  <Spinner animation="border" size="sm" /> Cargando...
                </span>
              )}
              {/* Botón de referencia para marzo 2025 + Antonio Puga */}
              <Button 
                variant="outline-info" 
                className="ms-2"
                onClick={() => {
                  setPeriodoSeleccionado('abril2025');
                  setMedioSeleccionado('58'); // ID de Antonio Puga
                  setMostrarPeriodoPersonalizado(false);
                  setTimeout(() => cargarDatosPeriodo('abril2025', '58'), 100);
                }}
              >
                <i className="bi bi-bookmark-check me-1"></i> Cargar referencia (Abril 2025 - Antonio Puga)
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {periodoMostrado && !isLoading && !error && (
        <Card className="mb-4 border-primary">
          <Card.Body className="d-flex align-items-center">
            <i className="bi bi-calendar-range fs-4 text-primary me-3"></i>
            <div>
              <h5 className="mb-0">Periodo analizado</h5>
              <p className="mb-0 text-primary fw-bold">{periodoMostrado}</p>
              {medioSeleccionado && mediosDisponibles.length > 0 && (
                <p className="mb-0 small text-muted">
                  Medio: {mediosDisponibles.find(m => m.id.toString() === medioSeleccionado)?.nombre || 'No especificado'}
                </p>
              )}
            </div>
          </Card.Body>
        </Card>
      )}
      
      {isLoading ? (
        <div className="text-center p-5">
          <Spinner animation="border" role="status" variant="primary" />
          <p className="mt-3">Cargando datos del dashboard...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : datosAvisos ? (
        <>
          <Row className="mb-4">
            <Col>
              <h4 className="mb-3">
                {periodoMostrado}
                {datosComparacion && (
                  <span className="ms-2 text-muted fs-6">
                    vs. {datosComparacion.titulo}
                  </span>
                )}
              </h4>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={3}>
              <Card className="h-100 shadow-sm">
                <Card.Body className="position-relative">
                  <h5 className="text-muted mb-3">Total Ventas</h5>
                  <h3>{formatearMoneda(datosAvisos.totalValor)}</h3>
                  
                  {datosComparacion && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center">
                        {calcularVariacion(datosAvisos.totalValor, datosComparacion.totalValor || 0).porcentaje > 0 ? (
                          <Badge bg="success" className="me-2">
                            <i className="bi bi-arrow-up-right"></i>
                            +{calcularVariacion(datosAvisos.totalValor, datosComparacion.totalValor || 0).porcentaje.toFixed(1)}%
                          </Badge>
                        ) : (
                          <Badge bg="danger" className="me-2">
                            <i className="bi bi-arrow-down-right"></i>
                            {calcularVariacion(datosAvisos.totalValor, datosComparacion.totalValor || 0).porcentaje.toFixed(1)}%
                          </Badge>
                        )}
                        <small className="text-muted">vs periodo anterior</small>
                      </div>
                      <div className="mt-1 small text-muted">
                        Anterior: {formatearMoneda(datosComparacion.totalValor || 0)}
                      </div>
                    </div>
                  )}
                  
                  <div className="position-absolute top-0 end-0 p-3">
                    <span className="bg-primary rounded-circle text-white d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                      <i className="bi bi-currency-dollar fs-4"></i>
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={3}>
              <Card className="h-100 shadow-sm">
                <Card.Body className="position-relative">
                  <h5 className="text-muted mb-3">Total Avisos</h5>
                  <h3>{datosAvisos.totalAvisos.toLocaleString('es-CL')}</h3>
                  
                  {datosComparacion && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center">
                        {calcularVariacion(datosAvisos.totalAvisos, datosComparacion.totalAvisos || 0).porcentaje > 0 ? (
                          <Badge bg="success" className="me-2">
                            <i className="bi bi-arrow-up-right"></i>
                            +{calcularVariacion(datosAvisos.totalAvisos, datosComparacion.totalAvisos || 0).porcentaje.toFixed(1)}%
                          </Badge>
                        ) : (
                          <Badge bg="danger" className="me-2">
                            <i className="bi bi-arrow-down-right"></i>
                            {calcularVariacion(datosAvisos.totalAvisos, datosComparacion.totalAvisos || 0).porcentaje.toFixed(1)}%
                          </Badge>
                        )}
                        <small className="text-muted">vs periodo anterior</small>
                      </div>
                      <div className="mt-1 small text-muted">
                        Anterior: {(datosComparacion.totalAvisos || 0).toLocaleString('es-CL')}
                      </div>
                    </div>
                  )}
                  
                  <div className="position-absolute top-0 end-0 p-3">
                    <span className="bg-info rounded-circle text-white d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                      <i className="bi bi-newspaper fs-4"></i>
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={3}>
              <Card className="h-100 shadow-sm">
                <Card.Body className="position-relative">
                  <h5 className="text-muted mb-3">Valor Promedio</h5>
                  <h3>
                    {formatearMoneda(datosAvisos.totalAvisos > 0 ? datosAvisos.totalValor / datosAvisos.totalAvisos : 0)}
                  </h3>
                  
                  {datosComparacion && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center">
                        {calcularVariacion(
                          datosAvisos.totalAvisos > 0 ? datosAvisos.totalValor / datosAvisos.totalAvisos : 0,
                          datosComparacion.totalAvisos && datosComparacion.totalAvisos > 0 ? (datosComparacion.totalValor || 0) / datosComparacion.totalAvisos : 0
                        ).porcentaje > 0 ? (
                          <Badge bg="success" className="me-2">
                            <i className="bi bi-arrow-up-right"></i>
                            +{calcularVariacion(
                              datosAvisos.totalAvisos > 0 ? datosAvisos.totalValor / datosAvisos.totalAvisos : 0,
                              datosComparacion.totalAvisos && datosComparacion.totalAvisos > 0 ? (datosComparacion.totalValor || 0) / datosComparacion.totalAvisos : 0
                            ).porcentaje.toFixed(1)}%
                          </Badge>
                        ) : (
                          <Badge bg="danger" className="me-2">
                            <i className="bi bi-arrow-down-right"></i>
                            {calcularVariacion(
                              datosAvisos.totalAvisos > 0 ? datosAvisos.totalValor / datosAvisos.totalAvisos : 0,
                              datosComparacion.totalAvisos && datosComparacion.totalAvisos > 0 ? (datosComparacion.totalValor || 0) / datosComparacion.totalAvisos : 0
                            ).porcentaje.toFixed(1)}%
                          </Badge>
                        )}
                        <small className="text-muted">vs periodo anterior</small>
                      </div>
                      <div className="mt-1 small text-muted">
                        Anterior: {formatearMoneda(datosComparacion.totalAvisos && datosComparacion.totalAvisos > 0 ? (datosComparacion.totalValor || 0) / datosComparacion.totalAvisos : 0)}
                      </div>
                    </div>
                  )}
                  
                  <div className="position-absolute top-0 end-0 p-3">
                    <span className="bg-warning rounded-circle text-white d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                      <i className="bi bi-graph-up-arrow fs-4"></i>
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={3}>
              <Card className="h-100 shadow-sm">
                <Card.Body className="position-relative">
                  <h5 className="text-muted mb-3">Avisos por día</h5>
                  <h3>
                    {datosAvisos.promedioAvisosPorDia ? datosAvisos.promedioAvisosPorDia.toFixed(1) : '0.0'}
                  </h3>
                  
                  {datosComparacion && datosComparacion.fechaFin && datosComparacion.fechaInicio && (
                    <div className="mt-2">
                      {(() => {
                        const diasCompPeriodo = differenceInDays(datosComparacion.fechaFin, datosComparacion.fechaInicio) + 1;
                        const promedioAvisosCompPorDia = datosComparacion.totalAvisos / diasCompPeriodo;
                        
                        return (
                          <>
                            <div className="d-flex align-items-center">
                              {calcularVariacion(
                                datosAvisos.promedioAvisosPorDia,
                                promedioAvisosCompPorDia
                              ).porcentaje > 0 ? (
                                <Badge bg="success" className="me-2">
                                  <i className="bi bi-arrow-up-right"></i>
                                  +{calcularVariacion(
                                    datosAvisos.promedioAvisosPorDia,
                                    promedioAvisosCompPorDia
                                  ).porcentaje.toFixed(1)}%
                                </Badge>
                              ) : (
                                <Badge bg="danger" className="me-2">
                                  <i className="bi bi-arrow-down-right"></i>
                                  {calcularVariacion(
                                    datosAvisos.promedioAvisosPorDia,
                                    promedioAvisosCompPorDia
                                  ).porcentaje.toFixed(1)}%
                                </Badge>
                              )}
                              <small className="text-muted">vs periodo anterior</small>
                            </div>
                            <div className="mt-1 small text-muted">
                              Anterior: {promedioAvisosCompPorDia.toFixed(1)}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  
                  <div className="position-absolute top-0 end-0 p-3">
                    <span className="bg-success rounded-circle text-white d-inline-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                      <i className="bi bi-calendar-check fs-4"></i>
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Gráficos principales */}
          <Row className="mb-4">
            <Col lg={12}>
              <Card className="mb-4">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">Tendencia de Ventas</h5>
                </Card.Header>
                <Card.Body className="p-2 p-md-3">
                  <div style={{ height: '500px', width: '100%' }}>
                    <Line 
                      data={datosGraficoTendencia} 
                      options={{
                        ...opcionesTendencia,
                        responsive: true,
                        maintainAspectRatio: false
                      }} 
                    />
                  </div>
                  
                  {datosComparacion && (
                    <Card className="mt-3 bg-light">
                      <Card.Body>
                        <h6 className="mb-3">Comparación con periodo anterior:</h6>
                        <Row>
                          <Col md={6}>
                            <div className="d-flex justify-content-between mb-2">
                              <span>Total ventas actual:</span>
                              <strong>{formatearMoneda(datosAvisos.totalValor)}</strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span>Total ventas anterior:</span>
                              <strong>{formatearMoneda(datosComparacion.totalValor || 0)}</strong>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span>Variación:</span>
                              <span className={
                                calcularVariacion(datosAvisos.totalValor, datosComparacion.totalValor || 0).porcentaje > 0 
                                  ? 'text-success' 
                                  : 'text-danger'
                              }>
                                <strong>
                                  {calcularVariacion(datosAvisos.totalValor, datosComparacion.totalValor || 0).porcentaje > 0 ? '+' : ''}
                                  {formatearMoneda(calcularVariacion(datosAvisos.totalValor, datosComparacion.totalValor || 0).valor)}
                                </strong>
                                {' '}
                                ({calcularVariacion(datosAvisos.totalValor, datosComparacion.totalValor || 0).porcentaje > 0 ? '+' : ''}
                                {calcularVariacion(datosAvisos.totalValor, datosComparacion.totalValor || 0).porcentaje.toFixed(1)}%)
                              </span>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="d-flex justify-content-between mb-2">
                              <span>Total avisos actual:</span>
                              <strong>{datosAvisos.totalAvisos.toLocaleString('es-CL')}</strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span>Total avisos anterior:</span>
                              <strong>{datosComparacion.totalAvisos.toLocaleString('es-CL')}</strong>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span>Variación:</span>
                              <span className={
                                calcularVariacion(datosAvisos.totalAvisos, datosComparacion.totalAvisos || 0).porcentaje > 0 
                                  ? 'text-success' 
                                  : 'text-danger'
                              }>
                                <strong>
                                  {calcularVariacion(datosAvisos.totalAvisos, datosComparacion.totalAvisos || 0).porcentaje > 0 ? '+' : ''}
                                  {calcularVariacion(datosAvisos.totalAvisos, datosComparacion.totalAvisos || 0).valor.toLocaleString('es-CL')}
                                </strong>
                                {' '}
                                ({calcularVariacion(datosAvisos.totalAvisos, datosComparacion.totalAvisos || 0).porcentaje > 0 ? '+' : ''}
                                {calcularVariacion(datosAvisos.totalAvisos, datosComparacion.totalAvisos || 0).porcentaje.toFixed(1)}%)
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Pestañas con análisis detallados */}
          <Tabs defaultActiveKey="ejecutivos" className="mb-4">
            <Tab eventKey="ejecutivos" title="Top Ejecutivos">
              <Row>
                <Col lg={7}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <h5 className="mb-0">Top 10 Ejecutivos por Ventas</h5>
                    </Card.Header>
                    <Card.Body>
                      <div style={{ height: '400px' }}>
                        <Bar 
                          data={datosGraficoEjecutivos} 
                          options={opcionesGraficoBarras} 
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={5}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <h5 className="mb-0">Detalle por Ejecutivo</h5>
                    </Card.Header>
                    <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Ejecutivo</th>
                            <th className="text-end">Avisos</th>
                            <th className="text-end">Valor Total</th>
                            <th className="text-end">Promedio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topEjecutivos.map((ejecutivo, index) => (
                            <tr key={index}>
                              <td>{ejecutivo.nombre}</td>
                              <td className="text-end">{ejecutivo.avisos}</td>
                              <td className="text-end">{formatearMoneda(ejecutivo.valor)}</td>
                              <td className="text-end">{formatearMoneda(ejecutivo.valor / ejecutivo.avisos)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="clientes" title="Top Clientes">
              <Row>
                <Col lg={7}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <h5 className="mb-0">Top 10 Clientes por Ventas</h5>
                    </Card.Header>
                    <Card.Body>
                      <div style={{ height: '400px' }}>
                        <Bar 
                          data={datosGraficoClientes} 
                          options={opcionesGraficoBarras} 
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={5}>
                  <Card>
                    <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Detalle de Clientes</h5>
                      <div>
                        <Form.Select 
                          size="sm" 
                          onChange={(e) => {
                            const criterioOrden = e.target.value;
                            let clientesOrdenados = [...topClientes];
                            
                            switch(criterioOrden) {
                              case 'avisos_desc':
                                clientesOrdenados.sort((a, b) => b.avisos - a.avisos);
                                break;
                              case 'avisos_asc':
                                clientesOrdenados.sort((a, b) => a.avisos - b.avisos);
                                break;
                              case 'valor_desc':
                                clientesOrdenados.sort((a, b) => b.valor - a.valor);
                                break;
                              case 'valor_asc':
                                clientesOrdenados.sort((a, b) => a.valor - b.valor);
                                break;
                              case 'nombre':
                                clientesOrdenados.sort((a, b) => a.nombre.localeCompare(b.nombre));
                                break;
                              default:
                                // Por defecto, ordenar por valor descendente
                                clientesOrdenados.sort((a, b) => b.valor - a.valor);
                            }
                            
                            setTopClientes(clientesOrdenados);
                          }}
                        >
                          <option value="valor_desc">Mayor Inversión</option>
                          <option value="avisos_desc">Mayor Cantidad de Avisos</option>
                          <option value="valor_asc">Menor Inversión</option>
                          <option value="avisos_asc">Menor Cantidad de Avisos</option>
                          <option value="nombre">Orden Alfabético</option>
                        </Form.Select>
                      </div>
                    </Card.Header>
                    <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <Tabs defaultActiveKey="todos" className="mb-3">
                        <Tab eventKey="todos" title="Todos los Clientes">
                          <Table striped bordered hover responsive>
                            <thead>
                              <tr className="bg-light">
                                <th>#</th>
                                <th>Cliente</th>
                                <th className="text-end">Avisos</th>
                                <th className="text-end">Valor Total</th>
                                <th className="text-end">Promedio</th>
                              </tr>
                            </thead>
                            <tbody>
                              {topClientes.map((cliente, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{cliente.nombre}</td>
                                  <td className="text-end">{cliente.avisos}</td>
                                  <td className="text-end">{formatearMoneda(cliente.valor)}</td>
                                  <td className="text-end">{formatearMoneda(cliente.valor / cliente.avisos)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Tab>
                        <Tab eventKey="top10valor" title="Top 10 Inversión">
                          <Table striped bordered hover responsive>
                            <thead>
                              <tr className="bg-light">
                                <th>#</th>
                                <th>Cliente</th>
                                <th className="text-end">Valor Total</th>
                                <th className="text-end">% del Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...topClientes]
                                .sort((a, b) => b.valor - a.valor)
                                .slice(0, 10)
                                .map((cliente, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{cliente.nombre}</td>
                                  <td className="text-end">{formatearMoneda(cliente.valor)}</td>
                                  <td className="text-end">
                                    {datosAvisos?.totalValor ? 
                                      (cliente.valor / datosAvisos.totalValor * 100).toFixed(1) : 0}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Tab>
                        <Tab eventKey="top10avisos" title="Top 10 Avisos">
                          <Table striped bordered hover responsive>
                            <thead>
                              <tr className="bg-light">
                                <th>#</th>
                                <th>Cliente</th>
                                <th className="text-end">Avisos</th>
                                <th className="text-end">% del Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...topClientes]
                                .sort((a, b) => b.avisos - a.avisos)
                                .slice(0, 10)
                                .map((cliente, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{cliente.nombre}</td>
                                  <td className="text-end">{cliente.avisos}</td>
                                  <td className="text-end">
                                    {datosAvisos?.totalAvisos ? 
                                      (cliente.avisos / datosAvisos.totalAvisos * 100).toFixed(1) : 0}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Tab>
                      </Tabs>
                    </Card.Body>
                    <Card.Footer className="text-muted">
                      <small>Total de clientes: {topClientes.length}</small>
                    </Card.Footer>
                  </Card>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="medios" title="Rendimiento por Medio">
              <Row>
                <Col lg={7}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <h5 className="mb-0">Distribución de Ventas por Medio</h5>
                    </Card.Header>
                    <Card.Body>
                      <div style={{ height: '400px' }}>
                        <Pie 
                          data={datosGraficoMedios} 
                          options={opcionesGraficos} 
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={5}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <h5 className="mb-0">Detalle por Medio</h5>
                    </Card.Header>
                    <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Medio</th>
                            <th className="text-end">Avisos</th>
                            <th className="text-end">Valor Total</th>
                            <th className="text-end">% Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rendimientoMedio.map((medio, index) => (
                            <tr key={index}>
                              <td>{medio.nombre}</td>
                              <td className="text-end">{medio.avisos}</td>
                              <td className="text-end">{formatearMoneda(medio.valor)}</td>
                              <td className="text-end">
                                {Math.round(medio.valor / datosAvisos?.totalValor * 100)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
          
          {/* Indicadores de desempeño */}
          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">Indicadores de Desempeño</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Card className="border-0 mb-3">
                        <Card.Body className="text-center">
                          <h6>Valor Promedio por Aviso</h6>
                          <h3>{formatearMoneda(datosAvisos?.promedioValor)}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="border-0 mb-3">
                        <Card.Body className="text-center">
                          <h6>Cantidad de Clientes</h6>
                          <h3>{topClientes.length}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="border-0 mb-3">
                        <Card.Body className="text-center">
                          <h6>Promedio Diario</h6>
                          <h3>{formatearMoneda(datosAvisos?.totalValor / datosAvisos?.diasEnPeriodo)}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : null}
    </Container>
  );
};

export default Dashboard; 